import { useEffect, useRef } from "react";
import {
  replacePinnedCatalog,
  warmPinnedCatalog,
} from "../store/Slices/MessageListSlice";
import {
  clearPinnedCatalogDisk,
  fetchPinnedMessages,
  isPinnedCatalogAuthoritative,
  readPinnedCatalogDisk,
  writePinnedCatalogDisk,
} from "../utils/fetchPinnedMessages";
import { useAppDispatch } from "../utils/hooks";

/**
 * Bootstrap pin bar from disk warm + dedicated get_pinned_messages.
 * Independent of tip / around / before / after history pages.
 */
export function usePinnedCatalog(chatId: number) {
  const dispatch = useAppDispatch();
  const reqGen = useRef(0);

  useEffect(() => {
    if (!chatId || chatId <= 0) return;

    const gen = ++reqGen.current;

    // Disk warm — avoid empty flash on soft reopen
    const warm = readPinnedCatalogDisk(chatId);
    if (warm?.length) {
      dispatch(warmPinnedCatalog(warm));
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetchPinnedMessages({ chat_id: chatId });
        if (cancelled || gen !== reqGen.current) return;
        if (Number(res?.chat_id) !== Number(chatId)) return;
        if (!isPinnedCatalogAuthoritative(res)) return;

        const pins = res.pinned_messages || {
          Records: [],
          Pagination: {
            total_pages: 0,
            total_records: 0,
            current_page: 1,
            records_per_page: 50,
          },
        };
        const total =
          Number(
            (pins.Pagination as any)?.totalRecords ??
              pins.Pagination?.total_records ??
              pins.Records?.length ??
              0,
          ) || 0;

        dispatch(
          replacePinnedCatalog({
            chat_id: chatId,
            pinned_messages: pins,
            complete: true,
          }),
        );

        if (total === 0) {
          clearPinnedCatalogDisk(chatId);
        } else {
          writePinnedCatalogDisk(chatId, pins.Records || []);
        }
      } catch {
        /* keep disk / previous catalog */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chatId, dispatch]);
}
